import React, { useState } from 'react';
import { Box, Typography, Paper, Grid, Divider, Chip, Stack, Avatar, List, ListItem, ListItemText, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';

import { useAdminGetCustomerDetails } from 'hooks/useCustomers';
import CustomerManagementMVP from './CustomerManagementMVP';
import WalletFundManagement from 'views/admin/components/WalletFundManagement';
import VirtualAccountManagement from 'views/admin/components/VirtualAccountManagement';

export default function CustomerDetailPage() {
    const { customerId } = useParams();
    const [customer, setCustomer] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const navigate = useNavigate();

    // 1. Fetch data
    const { data: customerRes, isLoading, isError, refetch } = useAdminGetCustomerDetails(customerId);

    // 2. Sync Query data to Local State when it arrives
    // 🔹 Sync state ONLY when data actually arrives or changes
    React.useEffect(() => {
        if (customerRes?.data) {
            setCustomer(customerRes.data);
        }
    }, [customerRes]); //

    if (isLoading || !customer) return <Box p={3}>Loading...</Box>;

    if (isError || !customer) {
        return (
            <Box p={3}>
                <Typography color="error">Failed to load customer details.</Typography>
            </Box>
        );
    }

    return (
        // 3. APPLY KEY HERE: This forces the entire page to remount
        <Box p={3}>
            <Typography variant="h4" fontWeight={700} mb={3}>
                Customer Profile: {customer.firstName} {customer.lastName}
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    {/* Customer Profile Card Content... */}
                    <Paper sx={{ p: 3, borderRadius: 3, background: 'primaryLight' }}>
                        <Stack direction="row" spacing={3} alignItems="center" mb={2}>
                            <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: '2rem' }}>
                                {customer.firstName?.[0] || 'C'}
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight={600}>
                                    {customer.firstName} {customer.lastName}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {customer.email}
                                </Typography>
                                <Stack direction="row" spacing={1} mt={1}>
                                    <Chip
                                        label={customer.isBanned ? 'BANNED' : 'Active'}
                                        color={customer.isBanned ? 'error' : 'success'}
                                        size="small"
                                    />
                                    <Chip
                                        label={`NIN: ${customer.ninStatus?.toUpperCase()?.replace('_', ' ') || 'NOT SUBMITTED'}`}
                                        color={
                                            customer.ninStatus === 'verified'
                                                ? 'success'
                                                : customer.ninStatus === 'failed'
                                                ? 'error'
                                                : 'warning'
                                        }
                                        variant="outlined"
                                        size="small"
                                    />
                                </Stack>
                            </Box>
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        {/* Contact Information */}
                        <Typography variant="h6" fontWeight={500} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} /> Contact & Stats
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Phone:
                                </Typography>
                                <Typography variant="body1">{customer.phoneNumber || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Joined Date:
                                </Typography>
                                <Typography variant="body1">{new Date(customer.createdAt).toLocaleDateString()}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Orders:
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                    {customer.orderCount}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Lifetime Spend:
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                    ₦{(customer.lifetimeSpend || 0).toLocaleString()}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {/* Address History */}
                        <Typography variant="h6" fontWeight={500} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} /> Saved Addresses ({customer.addresses?.length || 0})
                        </Typography>
                        {customer.addresses?.length > 0 ? (
                            <List dense>
                                {customer.addresses.slice(0, 3).map((addr, index) => (
                                    <ListItem key={index} disablePadding>
                                        <ListItemText primary={addr.label} secondary={`${addr.street}, ${addr.city}, ${addr.state}`} />
                                    </ListItem>
                                ))}
                                {customer.addresses.length > 3 && (
                                    <ListItem>
                                        <ListItemText primary={`+${customer.addresses.length - 3} more...`} />
                                    </ListItem>
                                )}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No saved addresses found.
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* 2. Admin Actions and Quick Stats */}
                <Grid item xs={12} md={4}>
                    <CustomerManagementMVP
                        // key={`mgmt-${refreshKey}`}
                        onUpdate={(newData) => {
                            // 1. Update local state immediately for instant UI feedback
                            setCustomer(newData);
                            // 2. Trigger the React Query hook to sync with the server
                            refetch();
                        }}
                        customerId={customerId}
                        initialData={{
                            firstName: customer.firstName,
                            lastName: customer.lastName,
                            phoneNumber: customer.phoneNumber,
                            status: customer.status,
                            deactivationReason: customer.deactivationReason || '',
                            isBanned: customer.isBanned,
                            ninStatus: customer.ninStatus || 'not_submitted'
                        }}
                    />

                    {/* 2. Wallet Management */}
                    <WalletFundManagement
                        userId={customerId}
                        userRole="user" // usr is the role for customers
                        currentWalletBalance={customer?.wallet?.availableBalance || 0} // Assume API returns this field
                    />

                    <Box mt={3}>
                        <VirtualAccountManagement walletId={customer?.wallet?._id} accountDetails={customer?.wallet?.virtualAccount} />
                    </Box>

                    {/* 3. Quick Stats (original component can stay here or be integrated) */}
                    <Paper sx={{ p: 3, borderRadius: 3, mt: 3 }}>{/* ... (Your latest order logic here) ... */}</Paper>
                </Grid>

                {/* Optional: Full Order History Table (link to AdminOrdersTable filtered by customerId) */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 3 }}>
                        <Typography variant="h6" fontWeight={600} mb={2}>
                            Full Order History (Viewable in Orders Tab)
                        </Typography>
                        {/* You would usually link to the main OrdersTable component here, pre-filtered by customerId */}
                        <Button variant="outlined" onClick={() => navigate(`/admin/orders?customer=${customerId}`)}>
                            View All {customer.orderCount} Orders
                        </Button>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
